import { DateTime } from 'luxon';
import React, { HTMLProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Bond } from '../../../../types/Bond';
import { mergedItems } from '../../../../utils/functions';
import { SectionDisclosure } from '../../../general/Disclosure';
import { mapTopText, mapSectionPages, SectionsContainer, Section } from '../comparison-bars/FundSectionFunctions';
import {
    ComparisonStickyCardDetailsBasicCharacteristics,
    ComparisonStickyCardDetailsDividendPayments,
    ComparisonStickyCardDetailsHistoricalReturns,
    ComparisonStickyCardDetailsHistoricalReturnsSmall,
    ComparisonStickyCardDetailsStrategy,
    ComparisonStickyCardDetailsUnderlyingBonds,
} from '../comparison-sticky-card/ComparisonStickyCardDetails';
import { ComparisonStickyCardDetailsRiskCharacteristics } from '../comparison-sticky-card/ComparisonStickyCardDetails';

export type TintedBond = Bond & {
    tint: string;
};

export const BondCompare: React.FC<
    {
        bonds: TintedBond[];
        startDate: string;
        endDate?: string;
    } & HTMLProps<HTMLDivElement>
> = ({ className, bonds, startDate, endDate = 'Nu' }) => {
    const { t } = useTranslation('funds');
    const BondTopText = bonds.map((section, index) => mapTopText(section, index, startDate, endDate));

    const BondStrategy = bonds.map((bond, index) => {
        return (
            <div
                key={bond.id + 'strategy'}
                className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            >
                <ComparisonStickyCardDetailsStrategy asset={bond} />
            </div>
        );
    });

    const BondCoreProperties = bonds.map((bond, index) => {
        return (
            <div
                key={bond.id + 'core'}
                className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            >
                <ComparisonStickyCardDetailsBasicCharacteristics asset={bond} />
            </div>
        );
    });

    const BondSpecifics = bonds.map((bond, index) => {
        return (
            <div
                key={bond.id + 'spec'}
                className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            >
                <ComparisonStickyCardDetailsUnderlyingBonds asset={bond} />
            </div>
        );
    });

    const BondRiskCharactaristic = bonds.map((bond, index) => {
        return (
            <div
                key={bond.id + 'risk'}
                className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            >
                <ComparisonStickyCardDetailsRiskCharacteristics asset={bond} />
            </div>
        );
    });

    const BondHistoricalReturns = bonds.map((bond, index) => {
        return (
            <div
                key={bond.id + 'returns'}
                className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            >
                <ComparisonStickyCardDetailsHistoricalReturns className="hidden sm:grid" asset={bond} />
                <ComparisonStickyCardDetailsHistoricalReturnsSmall className="sm:hidden" asset={bond} />
            </div>
        );
    });

    const BondDividends = bonds.map((bond, index) => {
        return (
            <div
                key={bond.id + 'divi'}
                className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            >
                <ComparisonStickyCardDetailsDividendPayments fund={bond} />
            </div>
        );
    });

    const BondSectors = bonds.map((bond, index) =>
        mapSectionPages({
            index,
            sections: [
                {
                    title: t('funds-comparison-screen-sectors'),
                    items: mergedItems(
                        bonds.map((b) => b.sectors || []),
                        bond.sectors || [],
                    ),
                    referenceDate: DateTime.fromISO(bond.referenceDateSectors),
                },
            ],
            appendix: 'remaining',
        }),
    );

    const BondRegionDistribution = bonds.map((bond, index) =>
        mapSectionPages({
            index,
            sections: [
                {
                    title: t('funds-comparison-screen-markets-ring'),
                    items: mergedItems(
                        bonds.map((b) => b.regions || []),
                        bond.regions || [],
                    ),
                    referenceDate: DateTime.fromISO(bond.referenceDateRegions),
                },
            ],
        }),
    );

    const BondCreditQuality = bonds.map((bond, index) => {
        return mapSectionPages({
            index,
            sections: [
                {
                    title: t('funds-comparison-screen-quality'),
                    items: mergedItems(
                        bonds.map((b) => b.quality || []),
                        bond.quality || [],
                    ),
                    referenceDate: DateTime.fromISO(bond.referenceDateQuality),
                },
            ],
            appendix: 'none',
        });
    });

    const BondMaturity = bonds.map((bond, index) =>
        mapSectionPages({
            index,
            sections: [
                {
                    title: t('funds-comparison-screen-duration'),
                    items: mergedItems(
                        bonds.map((b) => b.maturity || []),
                        bond.maturity || [],
                    ),
                    referenceDate: DateTime.fromISO(bond.referenceDateMaturity),
                },
            ],
            appendix: 'none',
        }),
    );

    const sections = [
        { title: t('funds-comparison-screen-investment-strategy'), nodes: BondStrategy, mobileOnly: true },
        { title: t('funds-comparison-screen-core-properties'), nodes: BondCoreProperties, mobileOnly: true },
        { title: t('funds-comparison-screen-underlying-bonds'), nodes: BondSpecifics, mobileOnly: true },
        { title: t('funds-comparison-screen-risk-characteristics'), nodes: BondRiskCharactaristic, mobileOnly: true },
        { title: t('funds-comparison-screen-historical-returns'), nodes: BondHistoricalReturns, mobileOnly: true },
        { title: t('funds-comparison-screen-dividend-payments'), nodes: BondDividends, mobileOnly: true },
        { title: t('funds-comparison-screen-sectors'), nodes: BondSectors },
        { title: t('funds-comparison-screen-markets'), nodes: BondRegionDistribution },
        { title: t('funds-comparison-screen-quality'), nodes: BondCreditQuality },
        { title: t('funds-comparison-screen-duration'), nodes: BondMaturity },
    ];

    return (
        <SectionsContainer className={className}>
            <Section key={sections[0].title} pages={BondTopText} />
            {sections.map((section, index) => {
                return (
                    <SectionDisclosure
                        title={section.title}
                        key={index}
                        className={`${section.mobileOnly === true ? 'xl:hidden' : ''}`}
                    >
                        <div className="flex flex-1 flex-col">
                            <Section key={index + ' ' + section.title} pages={section.nodes} />
                        </div>
                    </SectionDisclosure>
                );
            })}
        </SectionsContainer>
    );
};
